import { Card, Loading } from 'components';
import { Roles } from 'configs/roles';
import { Button } from 'modules';
import React, { ReactInstance, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useApproveCancelTransactionMutation,
  useGetTransactionDetailsQuery,
  useGetUserProfileQuery,
  useRequestCancelTransactionMutation,
} from 'services';
import { DetailData } from 'services/openapi';
import { getUserInfo, checkValidRole } from 'utils/helpers';
import { useReactToPrint } from 'utils/hooks';

import {
  ModalConfirmElement,
  PrintA4Invoice,
  PrintThermalInvoice,
} from '../Components';
import { dataDefined } from './config';
import {
  TransactionStatusColor,
  TransactionStatusEnum,
} from 'configs/constants';

const PayBillElectricDetailsFeature = () => {
  const navigate = useNavigate();

  const userInfo = getUserInfo();

  const isAdmin = checkValidRole([Roles.SUPER_ADMIN]);

  const { transactionCode } = useParams();

  const printA4Ref = useRef<ReactInstance>(null);

  const printThermalRef = useRef<ReactInstance>(null);

  const { data, isLoading, refetch } = useGetTransactionDetailsQuery(
    String(transactionCode),
  );

  const { data: personal } = useGetUserProfileQuery();

  // const { mutateAsync: mutateRefreshStatusTransactionAsync, isLoading: isLoadingRefreshStatusCallback, data: dataRefreshStatusCallback } = useRequestCancelTransactionMutation();

  const { mutateAsync: mutateRequestCancelTransactionAsync } =
    useRequestCancelTransactionMutation();

  const { mutateAsync: mutateApproveCancelTransactionAsync } =
    useApproveCancelTransactionMutation();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const detailsData = data?.data as DetailData;

  const handlePrintWebA4Invoice = useReactToPrint({
    content: () => printA4Ref?.current,
    copyStyles: true,
    removeAfterPrint: true,
    pageStyle: `
    @page {
        size: A4;
        margin: 40pt 15pt;

        .print-order-table-container {
          page-break-after: always;
      
          > main {
            display: table-row-group;
          }
      
          > thead {
            display: table-header-group;
          }
      
          > tfoot {
            display: table-footer-group;
          }
        }
      }
    `,
    documentTitle: '',
    suppressErrors: true,
  });

  const handlePrintThermalInvoice = useReactToPrint({
    content: () => printThermalRef?.current,
    copyStyles: true,
    removeAfterPrint: true,
    pageStyle: `
    @page {
        size: 225px 690px;
    }
    @page {
      size: auto;
    }
    .print-thermal-content {
      width: 225px;
      height: 690px;
      page-break-after: always;
      margin-top: 0;
      margin-right: 1rem;
      margin-bottom: 0;
      margin-left: 0;
      min-height: auto;
      content: ".";
    }
    `,
    documentTitle: '',
    suppressErrors: true,
  });

  // const handleRefreshStatusTransaction = async () => {
  //   try {
  //     await mutateRefreshStatusTransactionAsync({ transactionCode: String(transactionCode) });
  //   } catch (_) {
  //   }
  // };

  const handleRequestCancelTransaction = async () => {
    try {
      await mutateRequestCancelTransactionAsync({
        transactionCode: String(transactionCode),
      });

      toast.success('Yêu cầu hủy giao dịch thành công');
    } catch (_) {}
  };

  const handleApproveCancelTransaction = async () => {
    try {
      await mutateApproveCancelTransactionAsync({
        transactionCode: String(transactionCode),
      });
      toast.success('Hủy giao dịch thành công');
    } catch (_) {}
  };

  const handleOnClickCancelTransaction = async () => {
    setIsOpen(false);

    isAdmin &&
    detailsData?.transaction?.status == TransactionStatusEnum['Đang chờ hủy']
      ? await handleApproveCancelTransaction()
      : await handleRequestCancelTransaction();
  };

  const elementCancelTransaction = () => {
    if (
      isAdmin &&
      detailsData?.transaction?.status == TransactionStatusEnum['Đang chờ hủy']
    ) {
      return (
        <p>
          Xác nhận hủy giao dịch{' '}
          <span style={{ color: 'red' }}>{String(transactionCode)}</span> ?
        </p>
      );
    } else {
      return (
        <p>
          Bạn muốn gửi yêu cầu hủy giao dịch{' '}
          <span style={{ color: 'red' }}>{String(transactionCode)}</span> ?
        </p>
      );
    }
  };

  const titleCancelTransaction = () => {
    if (
      isAdmin &&
      detailsData?.transaction?.status == TransactionStatusEnum['Đang chờ hủy']
    ) {
      return 'Xác nhận hủy giao dịch';
    } else {
      return 'Gửi yêu cầu hủy';
    }
  };

  const labelApproveCancelTransaction = () => {
    if (
      isAdmin &&
      detailsData?.transaction?.status == TransactionStatusEnum['Đang chờ hủy']
    ) {
      return 'Xác nhận hủy';
    } else {
      return 'Gửi yêu cầu hủy';
    }
  };

  const renderHtml = () => {
    if (!detailsData) return <></>;

    return Object.keys(dataDefined).map((key, index) => {
      const parentData = detailsData?.[key as keyof DetailData];

      const childData = dataDefined[key as keyof typeof dataDefined];

      return (
        <div key={index} className='mb32'>
          <h2 className='mb20'>{childData?.label}</h2>

          {Array.isArray(childData?.value) ? (
            childData?.value?.map((child) => {
              const childValue =
                parentData?.[
                  child?.key as keyof DetailData[
                    | 'transaction'
                    | 'customer'
                    | 'execBy']
                ];
              const classNameStatus =
                child?.key == 'status'
                  ? TransactionStatusColor[childValue]
                  : '';
              if (!childValue) return <></>;

              return (
                <div className='row mb10' key={child?.key}>
                  <div className='col-5 col-md-3 mb5'>
                    <b>{child?.label}</b>:
                  </div>
                  <div className={`col-7 col-md-9 mb5 ${classNameStatus}`}>
                    {(child as any)?.format?.(childValue) || childValue}
                  </div>
                </div>
              );
            })
          ) : (
            <></>
          )}
        </div>
      );
    });
  };

  const now = new Date();

  const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);

  const someTime = new Date(detailsData?.transaction?.payAt);

  return (
    <>
      <Card className='container'>
        <Loading isLoading={isLoading}>
          {renderHtml()}
          <div className='textCenter'>
            {detailsData?.transaction?.status ==
              TransactionStatusEnum['Thành công'] && (
              <Button
                onClick={handlePrintWebA4Invoice}
                label='In A4'
                type='button'
                buttonType='success'
                className='ml5 mr5'
              />
            )}

            {detailsData?.transaction?.status ==
              TransactionStatusEnum['Thành công'] && (
              <Button
                onClick={handlePrintThermalInvoice}
                label='In nhiệt'
                type='button'
                buttonType='info'
                className='ml5 mr5'
              />
            )}
            {/* {detailsData?.transaction?.status == TransactionStatusEnum["Đang xử lý"] && <Button label="Kiểm tra trạng thái" onClick={handleRefreshStatusTransaction} type="button" buttonType="primary" className="ml5 mr0 pdr10 pdl10 pdt10 pdb10" isLoading={isLoadingRefreshStatusCallback} />} */}
            {someTime > fifteenMinutesAgo && (
              <>
                {detailsData?.transaction?.status ==
                TransactionStatusEnum['Đang chờ hủy']
                  ? isAdmin && (
                      <Button
                        onClick={() => setIsOpen(true)}
                        label='Duyệt yêu cầu hủy'
                        type='button'
                        buttonType='danger-outline'
                        className='ml5 mr5'
                      />
                    )
                  : detailsData?.transaction?.status ==
                      TransactionStatusEnum['Thành công'] &&
                    userInfo?.phoneNumber ==
                      detailsData?.execBy?.phoneNumber && (
                      <Button
                        onClick={() => setIsOpen(true)}
                        label='Gửi yêu cầu hủy'
                        type='button'
                        buttonType='danger-outline'
                        className='ml5 mr5'
                      />
                    )}
              </>
            )}

            <Button
              label='Trở về'
              onClick={() => navigate(-1)}
              type='button'
              buttonType='primary-outline'
              className='ml5 mr5'
            />
          </div>
        </Loading>
      </Card>

      <div style={{ display: 'none' }}>
        <PrintThermalInvoice
          data={{ ...detailsData, personalPhoneNo: personal?.phoneNumber }}
          ref={printThermalRef}
        />
        <PrintA4Invoice
          data={{ ...detailsData, personalPhoneNo: personal?.phoneNumber }}
          ref={printA4Ref}
        />
      </div>

      {/* <ModalConfirm isOpen={isOpen} onClose={() => setIsOpen(false)} onSuccess={handleOnClickCancelTransaction} /> */}
      <ModalConfirmElement
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSuccess={handleOnClickCancelTransaction}
        title={titleCancelTransaction()}
        labelApprove={labelApproveCancelTransaction()}
      >
        {elementCancelTransaction()}
      </ModalConfirmElement>
    </>
  );
};

export default PayBillElectricDetailsFeature;
